import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { withTranslation } from 'react-i18next';
import { Grid } from 'semantic-ui-react';
import _ from 'underscore';
import Utils from 'utils/Utils.js';
import LoadInline from '../loading/LoadInline.js';
import { fontSizes, fonts } from 'style';
import { ReactComponent as HeaderTrophyIcon } from 'img/cld2026-header-trophy.svg';
import { ReactComponent as HeaderUserIcon } from 'img/cld2026-header-user.svg';

const DEFAULT_THEME = {
  progressBarColor: '#E3BA5F',
  textColor: '#18181A',
  borderAccentColor: '#376761',
  checkmarkColor: '#376761',
};

const LEADERBOARD_TOP_OFFSET_VW = 14;
const LEADERBOARD_MARGIN_VW = 2;
const COLUMNS_COUNT = 4;
const MAX_ROWS_PER_COLUMN = 10;
const ROW_HEIGHT_PX = 56;

function resolveTheme(leaderboard) {
  const t = leaderboard?.displayTheme || {};
  const toUrl = (v) => (typeof v === 'string' ? v : v?.url);
  return {
    progressBarColor: t.progressBarColor || DEFAULT_THEME.progressBarColor,
    textColor: t.textColor || DEFAULT_THEME.textColor,
    borderAccentColor: t.borderAccentColor || DEFAULT_THEME.borderAccentColor,
    checkmarkColor: t.checkmarkColor || DEFAULT_THEME.checkmarkColor,
    backgroundTopUrl: toUrl(t.backgroundTop),
    backgroundBottomUrl: toUrl(t.backgroundBottom),
    backgroundBackgroundUrl: toUrl(t.backgroundBackground),
  };
}

function buildStyles(theme) {
  const { progressBarColor, textColor, borderAccentColor, checkmarkColor } = theme;
  return {
    root: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
    },
    backgroundBase: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      zIndex: 0,
    },
    backgroundTop: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      minWidth: '100%',
      objectFit: 'cover',
      objectPosition: 'top center',
      zIndex: 1,
    },
    backgroundBottom: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: 'auto',
      minWidth: '100%',
      objectFit: 'cover',
      objectPosition: 'bottom center',
      zIndex: 1,
    },
    segment: {
      position: 'absolute',
      zIndex: 10,
      top: `${LEADERBOARD_TOP_OFFSET_VW}vw`,
      left: `${LEADERBOARD_MARGIN_VW}vw`,
      right: `${LEADERBOARD_MARGIN_VW}vw`,
      bottom: `${LEADERBOARD_MARGIN_VW}vw`,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#fefefe',
      borderRadius: '12px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)',
      overflow: 'hidden',
      borderTop: `3px solid ${borderAccentColor}`,
    },
    cardContent: { flexShrink: 0 },
    title: {
      ...fonts.body,
      fontSize: '1.85vw',
      color: textColor,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      paddingTop: '1.25vw',
      paddingBottom: '0.75vw',
      lineHeight: 1.3,
      textAlign: 'center',
    },
    headerRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3vw',
      flexWrap: 'wrap',
      width: '92%',
      margin: '0 auto 0.9vw',
      paddingBottom: '0.9vw',
      borderBottom: '1px solid rgba(0,0,0,0.06)',
    },
    headerStat: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5vw',
      flexShrink: 0,
      fontSize: '1.15vw',
    },
    headerIconWrap: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1.35em',
      height: '1.35em',
      flexShrink: 0,
    },
    headerStatIcon: {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      display: 'block',
    },
    headerLabel: {
      fontSize: '1.15vw',
      lineHeight: 1.35,
      color: textColor,
      fontWeight: 500,
      letterSpacing: '0.01em',
    },
    leaderboardContainer: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      paddingTop: '0.5vw',
    },
    listArea: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      overflow: 'hidden',
      padding: '0 1.125vw',
      gap: '1vw',
    },
    columnWrapper: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#f8f9fa',
      paddingLeft: '3vw',
      paddingRight: '3vw',
      paddingTop: '1vw',
      marginBottom: '1vw',
      borderRadius: '8px',
    },
    column: {
      flex: '1 1 0',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    columnInner: {
      display: 'flex',
      flexDirection: 'column',
      flex: '1 1 0',
      minHeight: 0,
    },
    rowSlot: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      alignItems: 'stretch',
    },
    recordBarContainer: {
      position: 'relative',
      marginBottom: '0.15vw',
      paddingTop: 0,
      paddingBottom: 0,
      flexShrink: 0,
      minHeight: '40px',
      width: '100%',
      borderBottom: '1px solid rgba(0,0,0,0.04)',
    },
    recordBar: {
      position: 'relative',
      padding: '5px 8px',
      height: '48px',
      backgroundColor: 'transparent',
      borderTopRightRadius: '24px',
      borderBottomRightRadius: '24px',
    },
    myRow: { backgroundColor: 'transparent', borderRadius: '6px' },
    rank: {
      display: 'inline-block',
      height: '100%',
      fontSize: '1em',
      verticalAlign: 'middle',
      textAlign: 'right',
      position: 'relative',
      color: '#6b7280',
      fontWeight: 600,
    },
    recordBarBody: { left: 0, top: 0 },
    personInfo: {
      width: '100%',
      lineHeight: '1.1vw',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'nowrap',
    },
    personInfoName: {
      flex: '1 1 auto',
      minWidth: '10vw',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: '1.05vw',
      color: textColor,
      fontWeight: 500,
    },
    companyName: { fontSize: '0.8vw', fontStyle: 'italic' },
    icon: { position: 'relative', top: '2px', left: '-5px' },
    progressBar: {
      marginTop: '3px',
      height: '7.5px',
      width: '100%',
      backgroundColor: progressBarColor,
      borderRadius: '4px',
    },
    myProgressBar: { backgroundColor: '#fff', background: '#fff' },
    clickable: { cursor: 'pointer' },
    iconBadge: {
      verticalAlign: 'middle',
      width: fontSizes.body,
      height: fontSizes.body,
      marginLeft: '0.3vw',
    },
    scoreAndCheckmark: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      minWidth: '6.5vw',
      flexShrink: 0,
    },
    scoreText: { color: textColor, fontWeight: 600 },
    checkmarkSlot: {
      width: '1.2vw',
      minWidth: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    loaderContainer: {
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkmarkColor,
    borderAccentColor,
  };
}

const LeaderboardDisplay = ({
  t,
  leaderboard,
  targetPoints,
  column,
  busy,
  onClickChange,
  total,
  isMember,
  isClickable,
  getName,
  getCompany,
  dailyMetric,
  dailyGoal,
  formatLeaderboardValue,
  columns,
}) => {
  const listAreaRef = useRef(null);
  const [visibleRows, setVisibleRows] = useState(MAX_ROWS_PER_COLUMN);
  const [hasMeasured, setHasMeasured] = useState(false);

  const theme = useMemo(() => resolveTheme(leaderboard), [leaderboard]);
  const styles = useMemo(() => buildStyles(theme), [theme]);

  const updateVisibleRows = useCallback(() => {
    const el = listAreaRef.current;
    if (!el) return;
    const height = el.getBoundingClientRect().height;
    if (height > 0) {
      setHasMeasured(true);
      const rows = Math.floor(height / ROW_HEIGHT_PX);
      setVisibleRows((prev) => {
        const next = Math.min(MAX_ROWS_PER_COLUMN, Math.max(1, rows));
        return next !== prev ? next : prev;
      });
    }
  }, []);

  useEffect(() => {
    const el = listAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => updateVisibleRows());
    ro.observe(el);
    updateVisibleRows();
    return () => ro.disconnect();
  }, [leaderboard, updateVisibleRows]);

  useEffect(() => {
    if (!leaderboard) return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(updateVisibleRows);
    });
    return () => cancelAnimationFrame(raf);
  }, [leaderboard, updateVisibleRows]);

  useEffect(() => {
    if (!leaderboard) return;
    window.addEventListener('resize', updateVisibleRows);
    return () => window.removeEventListener('resize', updateVisibleRows);
  }, [leaderboard, updateVisibleRows]);

  const visibleColumns = useMemo(() => {
    if (!columns || !Array.isArray(columns)) return [[], [], [], []];
    const flat = _.flatten(columns).slice(0, COLUMNS_COUNT * MAX_ROWS_PER_COLUMN);
    const take = visibleRows * COLUMNS_COUNT;
    const visible = flat.slice(0, take);
    const perCol = visibleRows;
    return [
      visible.slice(0, perCol),
      visible.slice(perCol, perCol * 2),
      visible.slice(perCol * 2, perCol * 3),
      visible.slice(perCol * 3, perCol * 4),
    ];
  }, [columns, visibleRows]);

  if (busy) {
    return (
      <div style={styles.loaderContainer}>
        <LoadInline centered vcentered />
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {theme.backgroundBackgroundUrl && (
        <img style={styles.backgroundBase} src={theme.backgroundBackgroundUrl} alt="" aria-hidden />
      )}
      {theme.backgroundTopUrl && (
        <img style={styles.backgroundTop} src={theme.backgroundTopUrl} alt="" aria-hidden />
      )}
      {theme.backgroundBottomUrl && (
        <img style={styles.backgroundBottom} src={theme.backgroundBottomUrl} alt="" aria-hidden />
      )}

      {Boolean(leaderboard) && (
        <div style={styles.segment}>
          <div style={styles.cardContent}>
            <div style={styles.title}>
              {leaderboard.shareableTitle || (leaderboard.title ? `${leaderboard.title} Leaderboard` : 'Leaderboard')}
            </div>
            <div style={styles.headerRow}>
              <div style={styles.headerStat}>
                <span style={{ ...styles.headerIconWrap, color: styles.textColor }}>
                  <HeaderTrophyIcon style={styles.headerStatIcon} aria-hidden />
                </span>
                <span style={styles.headerLabel}>
                  {leaderboard.type === 'teams' ? 'Team Competition' : 'Climb the Leaderboard. Win Big!'}
                </span>
              </div>
              <div style={styles.headerStat}>
                <span style={{ ...styles.headerIconWrap, color: styles.textColor }}>
                  <HeaderUserIcon style={styles.headerStatIcon} aria-hidden />
                </span>
                <span style={styles.headerLabel}>
                  {total === 1
                    ? `${total} ${t(leaderboard.type === 'teams' ? 'Team' : 'Participant')}`
                    : `${total.toLocaleString()} ${t(leaderboard.type === 'teams' ? 'Teams' : 'Participants')}`}
                </span>
              </div>
              <div style={styles.headerStat}>
                <span style={styles.headerIconWrap}>
                  <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%', display: 'block' }} aria-hidden>
                    <circle cx="12" cy="12" r="10" fill={styles.checkmarkColor} />
                    <path
                      d="M9 12l2 2 4-4"
                      fill="none"
                      stroke="white"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span style={styles.headerLabel}>Step Goal Hit Today</span>
              </div>
            </div>
          </div>

          <div style={styles.leaderboardContainer}>
            <div ref={listAreaRef} style={styles.listArea}>
              <div
                style={{
                  display: 'flex',
                  flex: '1 1 0',
                  minWidth: 0,
                  gap: '1vw',
                  visibility: hasMeasured ? 'visible' : 'hidden',
                }}
              >
                {visibleColumns.map((records, colIndex) => (
                  <div key={colIndex} style={styles.columnWrapper}>
                    <div style={styles.column}>
                      <div style={styles.columnInner}>
                        {records &&
                          records.map((record) => {
                            const scope = record.team ? 'team' : 'userProfile';
                            return (
                              <div key={record.id} style={styles.rowSlot}>
                                <div
                                  style={{
                                    ...styles.recordBarContainer,
                                    ...(isMember(record) ? styles.myRow : {}),
                                    ...(isClickable(record) ? styles.clickable : {}),
                                  }}
                                  onClick={() => onClickChange(record)}
                                >
                                  <div style={styles.recordBar}>
                                    <Grid>
                                      <Grid.Row>
                                        <Grid.Column width={1} style={{ ...styles.rank, padding: 0 }}>
                                          {record.rank}
                                        </Grid.Column>
                                        <Grid.Column width={15} style={styles.recordBarBody}>
                                          <div style={styles.personInfo}>
                                            <span style={styles.personInfoName}>
                                              {getName(record[scope])}{' '}
                                              <span style={styles.companyName}>{getCompany(record[scope])}</span>
                                            </span>
                                            <span style={styles.scoreAndCheckmark}>
                                              <span style={{ marginRight: '0.2vw' }}>
                                                <i style={styles.icon} className={leaderboard.icon} />
                                                <span style={styles.scoreText}>
                                                  {formatLeaderboardValue(record, column)?.toLocaleString()}
                                                </span>
                                              </span>
                                              <span style={styles.checkmarkSlot}>
                                                {dailyMetric(record[scope]) >= dailyGoal(record[scope]) ? (
                                                  <svg
                                                    viewBox="0 0 24 24"
                                                    style={{
                                                      ...styles.iconBadge,
                                                      width: '1.1em',
                                                      height: '1.1em',
                                                      display: 'block',
                                                    }}
                                                    aria-hidden
                                                  >
                                                    <circle cx="12" cy="12" r="10" fill={styles.checkmarkColor} />
                                                    <path
                                                      d="M9 12l2 2 4-4"
                                                      fill="none"
                                                      stroke="white"
                                                      strokeWidth="2.5"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                    />
                                                  </svg>
                                                ) : null}
                                              </span>
                                            </span>
                                          </div>
                                          <div
                                            style={{
                                              ...styles.progressBar,
                                              ...(isMember(record) ? styles.myProgressBar : {}),
                                              width: record[scope].anonymous
                                                ? '0%'
                                                : `${Utils.getPercent(record[column], targetPoints)}%`,
                                            }}
                                          />
                                        </Grid.Column>
                                      </Grid.Row>
                                    </Grid>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withTranslation('leaderboards')(LeaderboardDisplay);
